import { memo, useState, useCallback, useEffect, useRef } from "react";
import { getImportJobStatus } from "@/infrastructure/product/productService";
import type { ImportJobStatus } from "@/domain/types";
import { colors, typography } from "@/design-system/tokens";

const POLL_INTERVAL = 5000;

export const ImportProgressBar = memo(function ImportProgressBar({
  jobId,
  onCompleted,
}: {
  jobId: string;
  onCompleted: () => void;
}) {
  const [job, setJob] = useState<ImportJobStatus | null>(null);
  const [visible, setVisible] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const fetchStatus = useCallback(async () => {
    try {
      const status = await getImportJobStatus(jobId);
      setJob(status);
      if (status.status === "COMPLETED" || status.status === "FAILED") {
        if (pollRef.current) clearInterval(pollRef.current);
        onCompleted();
        setTimeout(() => setVisible(false), 4000);
      }
    } catch {}
  }, [jobId, onCompleted]);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, POLL_INTERVAL);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchStatus]);

  if (!visible || !job) return null;

  const progress = job.totalRows > 0 ? Math.round((job.processed / job.totalRows) * 100) : 0;
  const isActive = job.status === "PENDING" || job.status === "PROCESSING";
  const isCompleted = job.status === "COMPLETED";
  const isFailed = job.status === "FAILED";

  const barColor = isActive
    ? (job.status === "PENDING" ? "#f59e0b" : "#3b82f6")
    : isCompleted ? "#16a34a" : colors.error;

  const label = isActive
    ? `${job.fileName} — ${progress}% (${job.processed.toLocaleString()}/${job.totalRows.toLocaleString()})`
    : isCompleted
    ? `${job.fileName} — ${job.created.toLocaleString()} created`
    : `${job.fileName} — Failed`;

  return (
    <div style={{ background: "#fff", border: `1px solid ${barColor}20`, borderRadius: "10px", padding: "12px 18px", display: "flex", flexDirection: "column", gap: "8px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isActive && <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: barColor, animation: "spin 1.5s linear infinite" }} />}
          {isCompleted && <span style={{ color: "#16a34a", fontSize: "14px" }}>✓</span>}
          {isFailed && <span style={{ color: colors.error, fontSize: "14px" }}>✗</span>}
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.medium, color: "#333" }}>
            {label}
          </span>
        </div>
        {isActive && (
          <span style={{ fontFamily: typography.fontFamily, fontSize: typography.size.sm, fontWeight: typography.weight.bold, color: barColor }}>
            {progress}%
          </span>
        )}
      </div>
      {isActive && (
        <div style={{ width: "100%", height: "4px", background: "#f0f0f0", borderRadius: "9999px", overflow: "hidden" }}>
          <div style={{ width: `${progress}%`, height: "100%", background: barColor, borderRadius: "9999px", transition: "width 0.5s ease" }} />
        </div>
      )}
    </div>
  );
});
